import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  Plane,
  Building2,
  Calendar,
  MapPin,
  Plus,
  Trash2,
  Save,
  Send,
  ArrowLeft,
  DollarSign,
  AlertTriangle,
  Sparkles,
  Link2,
  FileText,
  CreditCard,
  Eye,
  UploadCloud,
  FileUp,
  Paperclip,
  Loader2,
  ExternalLink,
  Image as ImageIcon,
  CheckCircle2,
  Utensils,
  Car,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import AttachmentPreviewModal from '../../components/travel/AttachmentPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelSelfBooking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [myApprovedRequests, setMyApprovedRequests] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Drive link write-in state
  const [driveLinkInput, setDriveLinkInput] = useState('');

  // Form General State
  const [formData, setFormData] = useState({
    linked_request_id: '',
    purpose: '',
    travel_type: 'flight',
    travel_category: 'Domestic',
    travel_mode: 'Flight',
    from_city: '',
    to_city: '',
    departure_date: new Date().toISOString().split('T')[0],
    return_date: new Date().toISOString().split('T')[0],
    department_id: '',
    department: '',
    project_id: '',
    project: '',
    cost_center: '',
    gl_account: '',
    all_attachments: [],
    remarks: ''
  });

  // Independently Selectable Itemized Expense Categories State
  const [ticketItems, setTicketItems] = useState([]);
  const [hotelItems, setHotelItems] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [localTravelItems, setLocalTravelItems] = useState([]);
  const [miscItems, setMiscItems] = useState([]);

  useEffect(() => {
    const empCompanyId = user?.company_id || user?.companyId || '';
    
    // Fetch departments
    axios.get(`${API_URL}/masters/departments?company_id=${empCompanyId}&active_only=true`, { withCredentials: true })
      .then(({ data }) => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => setDepartments([]));

    // Fetch projects if available
    axios.get(`${API_URL}/masters/projects?company_id=${empCompanyId}&active_only=true`, { withCredentials: true })
      .then(({ data }) => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]));

    // Fetch employee's approved travel requests for easy linkage
    axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true })
      .then(({ data }) => {
        const list = (data.requests || []).filter(r => ['approved', 'booked', 'completed'].includes(r.status));
        setMyApprovedRequests(list);
      })
      .catch(() => setMyApprovedRequests([]));
  }, [user]);

  // Dynamic category calculations using only entered expense items
  const ticketAmt = ticketItems.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
  const hotelAmt = hotelItems.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
  const foodAmt = foodItems.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
  const localAmt = localTravelItems.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
  const miscAmt = miscItems.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
  const totalClaimAmount = (ticketAmt + hotelAmt + foodAmt + localAmt + miscAmt).toFixed(2);

  // Independent Add Item handlers for each category
  const addExpenseItem = (category) => {
    const today = formData.departure_date || new Date().toISOString().split('T')[0];
    const newItem = {
      id: `${category}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      amount: '',
      date: today,
      vendor_ref: '',
      remarks: ''
    };

    if (category === 'ticket') setTicketItems(prev => [...prev, newItem]);
    else if (category === 'hotel') setHotelItems(prev => [...prev, newItem]);
    else if (category === 'food') setFoodItems(prev => [...prev, newItem]);
    else if (category === 'local_travel') setLocalTravelItems(prev => [...prev, newItem]);
    else if (category === 'misc') setMiscItems(prev => [...prev, newItem]);
  };

  const updateExpenseItem = (category, idx, field, val) => {
    const updater = (prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    };

    if (category === 'ticket') setTicketItems(updater);
    else if (category === 'hotel') setHotelItems(updater);
    else if (category === 'food') setFoodItems(updater);
    else if (category === 'local_travel') setLocalTravelItems(updater);
    else if (category === 'misc') setMiscItems(updater);
  };

  const removeExpenseItem = (category, idx) => {
    const remover = (prev) => prev.filter((_, i) => i !== idx);

    if (category === 'ticket') setTicketItems(remover);
    else if (category === 'hotel') setHotelItems(remover);
    else if (category === 'food') setFoodItems(remover);
    else if (category === 'local_travel') setLocalTravelItems(remover);
    else if (category === 'misc') setMiscItems(remover);
  };

  // Option A: Google Drive link attachment with validation
  const handleAddDriveLink = () => {
    let link = (driveLinkInput || '').trim();
    if (!link) {
      toast.error('Please enter a Google Drive link or cloud document URL.');
      return;
    }

    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      if (link.includes('drive.google.com') || link.includes('docs.google.com')) {
        link = `https://${link}`;
      } else {
        toast.error('Please enter a valid URL starting with http:// or https://');
        return;
      }
    }

    try {
      new URL(link);
    } catch (_) {
      toast.error('Invalid link format. Please provide a valid web URL.');
      return;
    }

    // Determine readable label
    let displayName = 'Google Drive Document';
    if (link.includes('drive.google.com') || link.includes('docs.google.com')) {
      displayName = 'Google Drive Receipt';
    } else {
      const pathSegs = link.split('?')[0].split('/');
      const lastSeg = pathSegs[pathSegs.length - 1];
      if (lastSeg && lastSeg.length > 2) {
        displayName = decodeURIComponent(lastSeg);
      }
    }

    const driveAttachment = {
      name: displayName,
      filename: displayName,
      url: link,
      download_url: link,
      type: link.toLowerCase().includes('.pdf') ? 'application/pdf' : 'link/url',
      is_drive_link: true,
      size_formatted: 'Google Drive Link',
    };

    setFormData(prev => ({
      ...prev,
      all_attachments: [...prev.all_attachments, driveAttachment]
    }));
    setDriveLinkInput('');
    toast.success('Google Drive link attached successfully.');
  };

  // Option B: Direct File Upload (PDF, JPG, JPEG, PNG up to 20 MB)
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingFiles(true);
    const newAttachments = [...formData.all_attachments.filter(a => a && (typeof a === 'object' || a.trim()))];

    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
        toast.error(`"${file.name}" has an unsupported format. Please upload PDF, JPG, JPEG, or PNG.`);
        continue;
      }
      // Maximum permitted file size: 20 MB per file
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds the 20 MB limit (${(file.size / (1024 * 1024)).toFixed(1)} MB).`);
        continue;
      }

      // Convert to base64 for immediate zero-latency preview
      const base64DataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });

      // Upload to backend GridFS storage
      let uploadedData = null;
      let uploadError = null;
      try {
        const fd = new FormData();
        fd.append('file', file);
        // Do NOT pass manual Content-Type: multipart/form-data header so Axios attaches correct boundary
        const { data } = await axios.post(`${API_URL}/travel-desk/upload-attachment`, fd, {
          withCredentials: true,
        });
        uploadedData = data;
      } catch (err) {
        console.warn('Backend attachment upload failed, falling back to data URL', err);
        uploadError = err.response?.data?.detail || err.message || 'Attachment upload failed';
      }

      if (uploadError && !uploadedData) {
        toast.error(`Upload error for ${file.name}: ${typeof uploadError === 'string' ? uploadError : 'Failed to upload'}`);
      }

      const sizeFormatted = file.size >= 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(file.size / 1024))} KB`;

      newAttachments.push({
        name: uploadedData?.name || file.name,
        filename: uploadedData?.filename || file.name,
        size: uploadedData?.size || file.size,
        size_formatted: uploadedData?.size_formatted || sizeFormatted,
        type: uploadedData?.type || file.type || (ext === 'pdf' ? 'application/pdf' : `image/${ext === 'jpg' ? 'jpeg' : ext}`),
        url: uploadedData?.url || base64DataUrl,
        download_url: uploadedData?.download_url || uploadedData?.url || base64DataUrl,
        file_id: uploadedData?.file_id,
        data_url: uploadedData ? null : base64DataUrl,
      });
    }

    setFormData(prev => ({ ...prev, all_attachments: newAttachments }));
    setUploadingFiles(false);
    e.target.value = '';
    toast.success('File(s) processed and attached successfully.');
  };

  const removeAttachmentRow = (idx) => {
    const updated = formData.all_attachments.filter((_, i) => i !== idx);
    setFormData(prev => ({ ...prev, all_attachments: updated }));
  };

  const handleSubmit = async (submitStatus) => {
    if (submitting) return;

    if (submitStatus !== 'draft') {
      if (!formData.purpose.trim()) {
        toast.error('Please enter the travel purpose.');
        return;
      }
      if (parseFloat(totalClaimAmount) <= 0) {
        toast.error('Total Reimbursement claim amount must be greater than zero.');
        return;
      }
    }

    try {
      setSubmitting(true);
      const cleanAttachments = formData.all_attachments.filter(a => {
        if (!a) return false;
        if (typeof a === 'string') return a.trim().length > 0;
        return true;
      });

      // Filter and sanitize items for each category
      const cleanTicketItems = ticketItems.filter(i => (parseFloat(i.amount) || 0) > 0 || i.vendor_ref || i.remarks);
      const cleanHotelItems = hotelItems.filter(i => (parseFloat(i.amount) || 0) > 0 || i.vendor_ref || i.remarks);
      const cleanFoodItems = foodItems.filter(i => (parseFloat(i.amount) || 0) > 0 || i.remarks);
      const cleanLocalItems = localTravelItems.filter(i => (parseFloat(i.amount) || 0) > 0 || i.remarks);
      const cleanMiscItems = miscItems.filter(i => (parseFloat(i.amount) || 0) > 0 || i.remarks);

      const payload = {
        ...formData,
        // Itemized lists
        ticket_items: cleanTicketItems,
        ticket_amount: ticketAmt,
        ticket_date: cleanTicketItems[0]?.date || formData.departure_date,
        ticket_vendor_ref: cleanTicketItems[0]?.vendor_ref || '',
        ticket_remarks: cleanTicketItems.map(i => i.remarks).filter(Boolean).join('; '),

        hotel_items: cleanHotelItems,
        hotel_amount: hotelAmt,
        hotel_date: cleanHotelItems[0]?.date || formData.departure_date,
        hotel_vendor_ref: cleanHotelItems[0]?.vendor_ref || '',
        hotel_remarks: cleanHotelItems.map(i => i.remarks).filter(Boolean).join('; '),

        food_items: cleanFoodItems,
        food_amount: foodAmt,
        food_date: cleanFoodItems[0]?.date || formData.departure_date,
        food_remarks: cleanFoodItems.map(i => i.remarks).filter(Boolean).join('; '),

        local_travel_items: cleanLocalItems,
        local_travel_amount: localAmt,
        local_travel_date: cleanLocalItems[0]?.date || formData.departure_date,
        local_travel_remarks: cleanLocalItems.map(i => i.remarks).filter(Boolean).join('; '),

        misc_items: cleanMiscItems,
        misc_amount: miscAmt,
        misc_date: cleanMiscItems[0]?.date || formData.departure_date,
        misc_remarks: cleanMiscItems.map(i => i.remarks).filter(Boolean).join('; '),

        total_claim_amount: parseFloat(totalClaimAmount),
        all_attachments: cleanAttachments,
        attachments: cleanAttachments,
        status: submitStatus
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/self-booking-claims`, payload, { withCredentials: true });

      if (data.success || data.request) {
        toast.success(submitStatus === 'draft' ? 'Reimbursement Claim saved as draft!' : 'Reimbursement Expense Claim submitted for approval!');
        navigate('/travel/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(d => d.msg || (typeof d === 'string' ? d : JSON.stringify(d))).join(', ') : (err.message || 'Failed to submit Reimbursement claim'));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto font-sans bg-slate-50/50 min-h-screen">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 text-white rounded-2xl p-6 shadow-xl border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/travel/dashboard')}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-all"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="p-2.5 bg-purple-500/20 text-purple-300 rounded-xl border border-purple-500/30">
              <Receipt className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Reimbursement</h1>
              <p className="text-xs text-purple-200">
                Submit itemized actual expenses for employee self-arranged travel, accommodation, meals &amp; conveyance.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded-xl flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Finance Reimbursement Workflow
          </span>
        </div>
      </div>

      {/* Main Form Body */}
      <div className="space-y-6">
        {/* Section 1: Travel Context & Details */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 border-b border-slate-100 pb-3">
            <Plane className="w-4 h-4 text-purple-600" /> Section 1: Travel Context &amp; Itinerary
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="block font-bold text-slate-700 mb-1">Link to Travel Request (Optional)</label>
              <select
                value={formData.linked_request_id}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const found = myApprovedRequests.find(r => r._id === selectedId);
                  if (found) {
                    setFormData({
                      ...formData,
                      linked_request_id: selectedId,
                      purpose: found.purpose || formData.purpose,
                      from_city: found.journey_details?.[0]?.from_city || formData.from_city,
                      to_city: found.journey_details?.[0]?.to_city || formData.to_city,
                      department: found.department || formData.department,
                      project: found.project || formData.project
                    });
                  } else {
                    setFormData({ ...formData, linked_request_id: selectedId });
                  }
                }}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium focus:border-purple-500"
              >
                <option value="">-- Direct Reimbursement Claim (No Prior Requisition) --</option>
                {myApprovedRequests.map(r => (
                  <option key={r._id} value={r._id}>
                    {r.request_number} — {r.purpose} ({r.journey_details?.[0]?.from_city || '—'} → {r.journey_details?.[0]?.to_city || '—'})
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block font-bold text-slate-700 mb-1">Travel Purpose / Trip Title *</label>
              <input
                type="text"
                placeholder="e.g. Client Onsite Meeting at Mumbai HQ"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium focus:border-purple-500"
                required
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Departure City (From) *</label>
              <input
                type="text"
                placeholder="e.g. Bengaluru"
                value={formData.from_city}
                onChange={(e) => setFormData({ ...formData, from_city: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Destination City (To) *</label>
              <input
                type="text"
                placeholder="e.g. Mumbai"
                value={formData.to_city}
                onChange={(e) => setFormData({ ...formData, to_city: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Primary Mode of Travel</label>
              <select
                value={formData.travel_mode}
                onChange={(e) => setFormData({ ...formData, travel_mode: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              >
                <option value="Flight">Flight</option>
                <option value="Train">Train</option>
                <option value="Cab">Cab / Taxi</option>
                <option value="Hotel Only">Hotel / Accommodation Only</option>
                <option value="Personal Vehicle">Personal Vehicle</option>
                <option value="Bus">Bus</option>
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Travel Departure Date</label>
              <input
                type="date"
                value={formData.departure_date}
                onChange={(e) => setFormData({ ...formData, departure_date: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Travel Return Date</label>
              <input
                type="date"
                value={formData.return_date}
                onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Department</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value, department_name: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              >
                <option value="">-- Select Department --</option>
                {departments.map(d => (
                  <option key={d._id || d.department_name} value={d.department_name || d.name}>{d.department_name || d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Project / Cost Center</label>
              <input
                type="text"
                placeholder="e.g. PRJ-GLOBAL-01"
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value, project_name: e.target.value })}
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-medium"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Independently Selectable 5-Category Expense Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 gap-2">
            <div>
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-600" /> Section 2: Itemized Expense Categories (Actual Expenses)
              </h3>
              <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                Add expense items independently under each relevant category. Only entered items are claimed.
              </p>
            </div>
            <span className="text-xs font-mono font-bold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-lg border border-purple-200 self-start sm:self-auto">
              Total Categories: 5
            </span>
          </div>

          <div className="space-y-6 text-xs">
            {/* 1. Travel Ticket Expense */}
            <div className="p-4 bg-purple-50/50 rounded-2xl border border-purple-100 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-purple-200/70 text-purple-800 rounded-lg font-bold text-[11px]">
                    <Plane className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <span className="font-bold text-purple-950 text-sm">1. Flight / Train / Travel Ticket Expense</span>
                    <span className="text-[10px] text-purple-600 block">Airfare, railway tickets, intercity bus tickets</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-purple-800 bg-purple-100/70 px-2.5 py-1 rounded-lg border border-purple-200">
                    Subtotal: ₹ {ticketAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                  <button
                    type="button"
                    onClick={() => addExpenseItem('ticket')}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Ticket Item
                  </button>
                </div>
              </div>

              {ticketItems.length === 0 ? (
                <div className="p-4 bg-white/70 border border-dashed border-purple-200 rounded-xl text-center text-slate-500 text-xs">
                  No ticket expenses added. Click <b className="text-purple-700">“+ Add Ticket Item”</b> if travel tickets were incurred.
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  {ticketItems.map((item, idx) => (
                    <div key={item.id || idx} className="p-3 bg-white rounded-xl border border-purple-200 shadow-xs grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Ticket Amount (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="0.00"
                          value={item.amount}
                          onChange={(e) => updateExpenseItem('ticket', idx, 'amount', e.target.value)}
                          className="w-full h-8 px-2.5 bg-slate-50 border border-slate-300 rounded-lg font-mono font-bold outline-none focus:border-purple-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Travel / Booking Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateExpenseItem('ticket', idx, 'date', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">PNR / Ticket Ref</label>
                        <input
                          type="text"
                          placeholder="e.g. 6E-PNR992"
                          value={item.vendor_ref}
                          onChange={(e) => updateExpenseItem('ticket', idx, 'vendor_ref', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg font-mono outline-none"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Carrier / Notes</label>
                        <input
                          type="text"
                          placeholder="e.g. IndiGo BLR-BOM"
                          value={item.remarks}
                          onChange={(e) => updateExpenseItem('ticket', idx, 'remarks', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeExpenseItem('ticket', idx)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Hotel / Accommodation Expense */}
            <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-blue-200/70 text-blue-800 rounded-lg font-bold text-[11px]">
                    <Building2 className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <span className="font-bold text-blue-950 text-sm">2. Hotel &amp; Accommodation Expense</span>
                    <span className="text-[10px] text-blue-600 block">Hotel room tariffs, lodging, accommodation charges</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-blue-800 bg-blue-100/70 px-2.5 py-1 rounded-lg border border-blue-200">
                    Subtotal: ₹ {hotelAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                  <button
                    type="button"
                    onClick={() => addExpenseItem('hotel')}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Hotel Item
                  </button>
                </div>
              </div>

              {hotelItems.length === 0 ? (
                <div className="p-4 bg-white/70 border border-dashed border-blue-200 rounded-xl text-center text-slate-500 text-xs">
                  No hotel expenses added. Click <b className="text-blue-700">“+ Add Hotel Item”</b> if accommodation was incurred.
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  {hotelItems.map((item, idx) => (
                    <div key={item.id || idx} className="p-3 bg-white rounded-xl border border-blue-200 shadow-xs grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Hotel Amount (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="0.00"
                          value={item.amount}
                          onChange={(e) => updateExpenseItem('hotel', idx, 'amount', e.target.value)}
                          className="w-full h-8 px-2.5 bg-slate-50 border border-slate-300 rounded-lg font-mono font-bold outline-none focus:border-blue-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Stay Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateExpenseItem('hotel', idx, 'date', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Hotel / Invoice Ref</label>
                        <input
                          type="text"
                          placeholder="e.g. INV-HTL-8821"
                          value={item.vendor_ref}
                          onChange={(e) => updateExpenseItem('hotel', idx, 'vendor_ref', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg font-mono outline-none"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Hotel Name &amp; City</label>
                        <input
                          type="text"
                          placeholder="e.g. Taj Lands End Mumbai"
                          value={item.remarks}
                          onChange={(e) => updateExpenseItem('hotel', idx, 'remarks', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeExpenseItem('hotel', idx)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 3. Food / Meals Expense */}
            <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-emerald-200/70 text-emerald-800 rounded-lg font-bold text-[11px]">
                    <Utensils className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <span className="font-bold text-emerald-950 text-sm">3. Food &amp; Meals Allowance</span>
                    <span className="text-[10px] text-emerald-600 block">Daily breakfast, lunch, dinner, client meal expenses</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-emerald-800 bg-emerald-100/70 px-2.5 py-1 rounded-lg border border-emerald-200">
                    Subtotal: ₹ {foodAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                  <button
                    type="button"
                    onClick={() => addExpenseItem('food')}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Meal Item
                  </button>
                </div>
              </div>

              {foodItems.length === 0 ? (
                <div className="p-4 bg-white/70 border border-dashed border-emerald-200 rounded-xl text-center text-slate-500 text-xs">
                  No meal expenses added. Click <b className="text-emerald-700">“+ Add Meal Item”</b> if food expenses were incurred.
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  {foodItems.map((item, idx) => (
                    <div key={item.id || idx} className="p-3 bg-white rounded-xl border border-emerald-200 shadow-xs grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Meal Amount (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="0.00"
                          value={item.amount}
                          onChange={(e) => updateExpenseItem('food', idx, 'amount', e.target.value)}
                          className="w-full h-8 px-2.5 bg-slate-50 border border-slate-300 rounded-lg font-mono font-bold outline-none focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Expense Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateExpenseItem('food', idx, 'date', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-5">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Meal Details / Restaurant &amp; Remarks</label>
                        <input
                          type="text"
                          placeholder="e.g. Dinner with Client Technical Team"
                          value={item.remarks}
                          onChange={(e) => updateExpenseItem('food', idx, 'remarks', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeExpenseItem('food', idx)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 4. Local Travel / Conveyance Expense */}
            <div className="p-4 bg-amber-50/50 rounded-2xl border border-amber-100 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-amber-200/70 text-amber-800 rounded-lg font-bold text-[11px]">
                    <Car className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <span className="font-bold text-amber-950 text-sm">4. Local Travel &amp; Conveyance (Cab/Taxi)</span>
                    <span className="text-[10px] text-amber-600 block">Airport cabs, local taxis, auto rickshaw, city conveyance</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-amber-800 bg-amber-100/70 px-2.5 py-1 rounded-lg border border-amber-200">
                    Subtotal: ₹ {localAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                  <button
                    type="button"
                    onClick={() => addExpenseItem('local_travel')}
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Conveyance Item
                  </button>
                </div>
              </div>

              {localTravelItems.length === 0 ? (
                <div className="p-4 bg-white/70 border border-dashed border-amber-200 rounded-xl text-center text-slate-500 text-xs">
                  No conveyance expenses added. Click <b className="text-amber-700">“+ Add Conveyance Item”</b> if local travel was incurred.
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  {localTravelItems.map((item, idx) => (
                    <div key={item.id || idx} className="p-3 bg-white rounded-xl border border-amber-200 shadow-xs grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Conveyance Amount (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="0.00"
                          value={item.amount}
                          onChange={(e) => updateExpenseItem('local_travel', idx, 'amount', e.target.value)}
                          className="w-full h-8 px-2.5 bg-slate-50 border border-slate-300 rounded-lg font-mono font-bold outline-none focus:border-amber-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Expense Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateExpenseItem('local_travel', idx, 'date', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-5">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Route / Cab Provider &amp; Details</label>
                        <input
                          type="text"
                          placeholder="e.g. Uber Airport to Hotel & Client Site"
                          value={item.remarks}
                          onChange={(e) => updateExpenseItem('local_travel', idx, 'remarks', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeExpenseItem('local_travel', idx)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 5. Miscellaneous Expense */}
            <div className="p-4 bg-slate-100/70 rounded-2xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-slate-300/80 text-slate-800 rounded-lg font-bold text-[11px]">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </span>
                  <div>
                    <span className="font-bold text-slate-900 text-sm">5. Miscellaneous / Incidentals</span>
                    <span className="text-[10px] text-slate-500 block">Wi-Fi, toll charges, baggage fees, laundry, stationeries</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-slate-800 bg-white px-2.5 py-1 rounded-lg border border-slate-300">
                    Subtotal: ₹ {miscAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                  <button
                    type="button"
                    onClick={() => addExpenseItem('misc')}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Misc Item
                  </button>
                </div>
              </div>

              {miscItems.length === 0 ? (
                <div className="p-4 bg-white/70 border border-dashed border-slate-300 rounded-xl text-center text-slate-500 text-xs">
                  No miscellaneous expenses added. Click <b className="text-slate-700">“+ Add Misc Item”</b> if incidentals were incurred.
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  {miscItems.map((item, idx) => (
                    <div key={item.id || idx} className="p-3 bg-white rounded-xl border border-slate-300 shadow-xs grid grid-cols-1 md:grid-cols-12 gap-2.5 items-end">
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Misc Amount (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="0.00"
                          value={item.amount}
                          onChange={(e) => updateExpenseItem('misc', idx, 'amount', e.target.value)}
                          className="w-full h-8 px-2.5 bg-slate-50 border border-slate-300 rounded-lg font-mono font-bold outline-none focus:border-slate-500"
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Expense Date</label>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateExpenseItem('misc', idx, 'date', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-5">
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Item Description / Purpose</label>
                        <input
                          type="text"
                          placeholder="e.g. Airport Lounge Wi-Fi, Tolls, Porter Charges"
                          value={item.remarks}
                          onChange={(e) => updateExpenseItem('misc', idx, 'remarks', e.target.value)}
                          className="w-full h-8 px-2 bg-slate-50 border border-slate-300 rounded-lg outline-none"
                        />
                      </div>
                      <div className="md:col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeExpenseItem('misc', idx)}
                          className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Live Summary & Total Calculation Display */}
        <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white rounded-2xl p-6 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4 border border-slate-800">
          <div className="space-y-1">
            <div className="text-xs font-bold text-purple-300 uppercase tracking-wider">Total Reimbursement Claim Amount</div>
            <div className="text-3xl font-black text-amber-400 font-mono">
              ₹ {parseFloat(totalClaimAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-slate-300">
              Sum of Ticket (₹{ticketAmt.toLocaleString('en-IN')}) + Hotel (₹{hotelAmt.toLocaleString('en-IN')}) + Food (₹{foodAmt.toLocaleString('en-IN')}) + Conveyance (₹{localAmt.toLocaleString('en-IN')}) + Misc (₹{miscAmt.toLocaleString('en-IN')})
            </div>
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleSubmit('draft')}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl flex items-center gap-2 border border-slate-700 transition-all"
            >
              <Save className="w-4 h-4 text-amber-400" /> Save Draft
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => handleSubmit('submitted')}
              className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-slate-950 font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit Claim for Reimbursement
            </button>
          </div>
        </div>

        {/* Section 4: Invoice / Bill Attachments & Remarks */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-5 text-xs">
          <div className="border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Link2 className="w-4 h-4 text-blue-600" /> Section 4: Invoice / Bill Attachments &amp; Remarks
            </h3>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">
              Support your claim with invoices, tickets, receipts, or Google Drive sharing links.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Option A: Google Drive Link */}
            <div className="p-4 bg-indigo-50/40 rounded-xl border border-indigo-100 space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="p-1 bg-indigo-100 text-indigo-700 rounded">
                    <Link2 className="w-3.5 h-3.5" />
                  </div>
                  <label className="font-bold text-indigo-950 text-xs">Option A – Google Drive / Cloud Link</label>
                </div>
                <p className="text-[11px] text-slate-500 mb-2">
                  Paste a shareable Google Drive, Dropbox, or OneDrive URL for invoices and tickets.
                </p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="https://drive.google.com/file/d/..."
                    value={driveLinkInput}
                    onChange={(e) => setDriveLinkInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDriveLink();
                      }
                    }}
                    className="flex-1 h-9 px-3 bg-white border border-slate-300 rounded-xl text-xs font-mono outline-none focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddDriveLink}
                    className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" /> Attach Link
                  </button>
                </div>
              </div>
              <span className="text-[10px] text-indigo-700 font-medium">Validates URL format automatically. Preview available inline.</span>
            </div>

            {/* Option B: Direct File Upload */}
            <div className="p-4 bg-purple-50/40 rounded-xl border border-purple-100 space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-purple-100 text-purple-700 rounded">
                      <FileUp className="w-3.5 h-3.5" />
                    </div>
                    <label className="font-bold text-purple-950 text-xs">Option B – Upload File Directly</label>
                  </div>
                  <span className="text-[10px] text-purple-700 font-mono font-bold">Max 20 MB/file</span>
                </div>
                <p className="text-[11px] text-slate-500 mb-2">
                  Supports PDF, JPG, JPEG, and PNG receipt files stored securely in GridFS.
                </p>

                <div className="border-2 border-dashed border-purple-200 hover:border-purple-400 bg-white hover:bg-purple-50/30 rounded-xl p-3 transition-all text-center">
                  <label className="cursor-pointer flex items-center justify-center gap-2">
                    {uploadingFiles ? (
                      <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                    ) : (
                      <UploadCloud className="w-4 h-4 text-purple-600" />
                    )}
                    <span className="font-bold text-purple-700 text-xs hover:underline">
                      {uploadingFiles ? 'Uploading receipts...' : 'Click to select files (or drop here)'}
                    </span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,image/png,image/jpeg,application/pdf"
                      onChange={handleFileUpload}
                      disabled={uploadingFiles}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              <span className="text-[10px] text-purple-700 font-medium">Auto-validated on both frontend &amp; backend.</span>
            </div>
          </div>

          {/* List of Attached Bills & Receipts */}
          {formData.all_attachments.length > 0 && (
            <div className="space-y-2 pt-2">
              <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Attached Receipts &amp; Invoices ({formData.all_attachments.length}):
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {formData.all_attachments.map((att, idx) => {
                  const isObj = att && typeof att === 'object';
                  const attName = isObj ? (att.name || att.filename || `Receipt #${idx + 1}`) : (typeof att === 'string' && att.trim() ? att : `Link #${idx + 1}`);
                  const isPdf = attName.toLowerCase().endsWith('.pdf') || (isObj && att.type?.includes('pdf'));
                  const isDrive = Boolean(isObj && att.is_drive_link) || (typeof att === 'string' && (att.includes('drive.google.com') || att.includes('docs.google.com')));

                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl gap-2 hover:bg-slate-100/90 transition-colors shadow-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className={`p-1.5 rounded-lg shrink-0 ${
                          isDrive ? 'bg-indigo-100 text-indigo-700' : isPdf ? 'bg-rose-100 text-rose-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {isDrive ? (
                            <Link2 className="w-4 h-4" />
                          ) : isPdf ? (
                            <FileText className="w-4 h-4" />
                          ) : (
                            <ImageIcon className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-slate-800 truncate text-xs" title={attName}>
                            {attName}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                            <span>{isDrive ? 'Google Drive Link' : isPdf ? 'PDF Receipt' : 'Image Receipt'}</span>
                            {isObj && att.size_formatted && (
                              <>
                                <span>•</span>
                                <span>{att.size_formatted}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewAttachment(att);
                            setIsPreviewOpen(true);
                          }}
                          className="px-2 py-1 bg-white hover:bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors"
                          title="Preview Bill"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        <button
                          type="button"
                          onClick={() => removeAttachmentRow(idx)}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Remove attachment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-2">
            <label className="block font-bold text-slate-700 mb-1">Additional Employee Remarks / Explanation</label>
            <textarea
              rows="3"
              placeholder="Provide any additional explanation for travel expense claims..."
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              className="w-full p-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
            />
          </div>

          {/* Action buttons at bottom */}
          <div className="pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-500 font-medium">
              Total Claimed: <span className="font-bold font-mono text-purple-700 text-sm">₹{parseFloat(totalClaimAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit('draft')}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-2 border border-slate-300 transition-all cursor-pointer"
              >
                <Save className="w-4 h-4 text-amber-500" /> Save Draft
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit('submitted')}
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all cursor-pointer"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit Reimbursement Claim
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        attachment={previewAttachment}
        title="Bill / Receipt Preview"
      />
    </div>
  );
};

export default TravelSelfBooking;
